'use client'

import Script from 'next/script'
import { ReactNode, useEffect, useRef, useState } from 'react'

import { useAmazonState } from '@/components/hooks/useAmazonState'
import { getAmazonPayAdditionalPaymentButtonParams } from '@/serverActions/getAmazonPayAdditionalPaymentButtonParams'
import { AmazonPayButtonParamsType } from '@/serverActions/getAmazonPayButtonParams'
import { addParameters } from '@/utils/urlUtils'

const AmazonPayAdditionalPaymentButton = ({
  orderId,
  redirectUrl,
  children,
  productType,
}: {
  orderId: string
  redirectUrl: string
  children: ReactNode
  productType: 'PayAndShip' | 'PayOnly'
}) => {
  const [amazonPayButtonParams, setAmazonPayParams] = useState<AmazonPayButtonParamsType | null>(null)
  const [rendered, setRendered] = useState(false)
  const amazonPayButtonRef = useRef<HTMLDivElement>(null)
  const { getState } = useAmazonState('additionalPaymentButtonSessionId')

  useEffect(() => {
    getAmazonPayAdditionalPaymentButtonParams({
      orderId,
      redirectUrl: addParameters(redirectUrl, {
        state: getState(),
        browsing: '1',
      }),
      productType,
    }).then((amazonPayButtonParams) => {
      if (amazonPayButtonParams != null) {
        setAmazonPayParams(amazonPayButtonParams)
      }
    })
  }, [redirectUrl])

  useEffect(() => {
    if (rendered && amazonPayButtonParams != null) {
      const amazonPayButton = amazonPayButtonRef.current
      if (amazonPayButton != null) {
        if (amazonPayButton.shadowRoot != null) {
          const shadowRoot = amazonPayButton.shadowRoot
          shadowRoot.innerHTML += ''
        }
      }
    }
  }, [rendered, amazonPayButtonParams])

  if (amazonPayButtonParams == null) {
    return <></>
  }

  return (
    <>
      <div
        id="AmazonPayButton"
        ref={amazonPayButtonRef}
        style={{ height: '8rem', margin: '0 auto', display: 'block' }}
      />
      {children}
      <Script
        src={'https://static-fe.payments-amazon.com/checkout.js'}
        onReady={() => {
          // eslint-disable-next-line no-undef
          amazon.Pay.renderButton('#AmazonPayButton', {
            merchantId: amazonPayButtonParams.amazonPaySellerId,
            ledgerCurrency: 'JPY',
            sandbox: amazonPayButtonParams.amazonPayIsSandbox,
            checkoutLanguage: 'ja_JP',
            productType,
            placement: 'Cart',
            buttonColor: 'Gold',
            createCheckoutSessionConfig: {
              payloadJSON: amazonPayButtonParams.amazonPayButtonPayload,
              signature: amazonPayButtonParams.amazonPayButtonSignature,
              publicKeyId: amazonPayButtonParams.amazonPayPublicKeyId,
            },
          })

          setRendered(true)
        }}
      />
    </>
  )
}

export default AmazonPayAdditionalPaymentButton
